import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams, useNavigate, Navigate } from 'react-router-dom'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import LockIcon from '@mui/icons-material/Lock'
import { useIdentity, useIsUser } from 'pages/Identity/Login'
import PendingReviewBanner from 'src/components/PendingReviewBanner'
import UpgradePlanDialog from 'src/components/UpgradePlanDialog'
import { fetchHospitalJobs } from 'src/redux/jobs'
import { fetchCandidateMatchList } from 'src/redux/candidate'
import CandidateMatchRow from 'pages/Hospitals/Jobs/CandidateMatchRow'

const DEFAULT_ROWS_PER_PAGE = 25
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50]

const QUALITY_CHIP_CONFIGS = [
  { key: 'excellent', label: 'Excellent', getStyle: theme => ({ backgroundColor: theme.palette.success.main, color: 'white' }) },
  { key: 'strong', label: 'Strong', getStyle: theme => ({ backgroundColor: theme.palette.info.main, color: 'white' }) },
  { key: 'potential', label: 'Potential', getStyle: theme => ({ backgroundColor: theme.palette.grey[500], color: 'white' }) },
]

const EmployerCandidateMatchListPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const [searchParams] = useSearchParams()
  const jobId = searchParams.get('jobId')

  const { employerId } = useIdentity()
  const { isEmployerUser, isSuper, registrationStatus, hasActiveSubscription } = useIsUser()
  const { jobsList } = useSelector(state => state.jobs)
  const candidateState = useSelector(state => state.candidate)

  useEffect(() => {
    if (employerId) {
      dispatch(fetchHospitalJobs({ employerId }))
    }
  }, [dispatch, employerId])

  const job = useMemo(
    () => jobsList?.find(j => String(j.id) === jobId),
    [jobsList, jobId],
  )

  useEffect(() => {
    if (jobId) {
      dispatch(fetchCandidateMatchList({ jobId }))
    }
  }, [dispatch, jobId])

  const matchedCandidates = candidateState.list || []

  const counts = useMemo(() => {
    const result = { total: 0, excellent: 0, strong: 0, potential: 0 }
    matchedCandidates.forEach(candidate => {
      result.total++
      const tier = candidate.matchTier?.toLowerCase()
      if (tier in result) result[tier]++
    })
    return result
  }, [candidateState.list])

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)

  const isPaywalled = !isSuper && !hasActiveSubscription

  useEffect(() => {
    setPage(0)
  }, [candidateState.list])

  const paginatedCandidates = useMemo(
    () => matchedCandidates.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
    [candidateState.list, page, rowsPerPage],
  )

  const isPendingReview = registrationStatus === 'Pending'


  if (!isEmployerUser) {
    return <Navigate to='/' replace />
  }

  if (!jobId) {
    return <Navigate to='/jobs' replace />
  }

  const jobLocation = [job?.city, job?.state].filter(Boolean).join(', ')
  const jobSpecialtyNames = (job?.specialties || []).map(s => s.name).join(', ')

  return (
    <Container maxWidth='lg' sx={{ py: 3 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/jobs')}
        sx={{ mb: 2 }}
      >
        Back to Jobs
      </Button>

      {!job ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 3 }}>
            <Typography variant='h4'>{job.title}</Typography>
            {(jobLocation || jobSpecialtyNames) && (
              <Typography variant='body1' color='text.secondary' sx={{ mt: 0.5 }}>
                {[jobLocation, jobSpecialtyNames].filter(Boolean).join(' — ')}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 600, mr: 1 }}>
              {counts.total} Candidate Matches
            </Typography>
            {QUALITY_CHIP_CONFIGS.filter(({ key }) => counts[key] > 0).map(({ key, label, getStyle }) => (
              <Chip
                key={key}
                label={`${counts[key]} ${label}`}
                size='small'
                sx={{
                  ...getStyle(theme),
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                }}
              />
            ))}
          </Box>

          {isPendingReview ? (
            <>
              <PendingReviewBanner />
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <Typography variant='body1' color='text.secondary'>
                  Available after verification.
                </Typography>
              </Box>
            </>
          ) : isPaywalled ? (
            <Box
              sx={{
                py: 6,
                textAlign: 'center',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                mt: 2,
              }}
            >
              <LockIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
              <Typography variant='h6' fontWeight={600} gutterBottom>
                Upgrade to view candidate details
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 480, mx: 'auto', mb: 3 }}>
                Subscribe to access candidate profiles, contact information, and advanced search filters.
              </Typography>
              <Button
                variant='contained'
                size='large'
                onClick={() => setShowUpgradeDialog(true)}
              >
                View Plans
              </Button>
            </Box>
          ) : candidateState.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : matchedCandidates.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant='body1' color='text.secondary'>
                No matching candidates found for this job.
              </Typography>
            </Box>
          ) : (
            <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Match</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Match Reasons</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Experience</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Specialty</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCandidates.map(candidate => (
                    <CandidateMatchRow
                      key={candidate.id}
                      candidate={candidate}
                      job={job}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component='div'
              count={matchedCandidates.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
            </>
          )}
        </>
      )}

      <UpgradePlanDialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
      />
    </Container>
  )
}

export default EmployerCandidateMatchListPage
